import { getChainInfo } from '@spritz-finance/sdk'
import { Body, Button, Form, Icon, OpenNewSvg, Spacer } from '@spritz-finance/ui'
import React, { useEffect, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'
import {
    ConfirmationDialog,
    ConfirmationDialogMethods,
} from '../../../../../components/ConfirmationDialog'
import { FullAccountSummary } from '../../../../../components/FullAccount'
import { Column } from '../../../../../components/Layout/Column'
import { Row } from '../../../../../components/Layout/Row'
import { BillType } from '../../../../../graphql/generated/graphql'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { DirectPayType, useDirectPayContext } from '../context/DirectPayContext'
import { TokenSelect } from '../TokenSelect'
import { TransactionLogs } from '../TransactionLogs'
import { CreateDirectPayFormData, usePayBill } from '../usePayBill'
import { BalancesIndicator, DelayIndicator } from './BalancesIndicator'
import { FormSubmitButton } from './FormSubmitButton'
import { PaymentAmount } from './PaymentAmount'
import { TokenSelectEmptyState } from './TokenSelectEmptyState'

export const FormItemHeader = styled.div`
    margin-bottom: 4px;
    padding: 0px 16px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const useSelectToken = () => {
    const { tokenBalances, dispatch } = useDirectPayContext()
    const selectedTokenAddress = useRef<string | null>(null)
    const methods = useForm<CreateDirectPayFormData>({
        defaultValues: { tokenAddress: tokenBalances[0]?.address },
    })

    const formSelectedTokenAddress = methods.watch('tokenAddress')

    useEffect(() => {
        if (formSelectedTokenAddress === selectedTokenAddress.current) return
        selectedTokenAddress.current = formSelectedTokenAddress
        const token = tokenBalances.find((t) => t.address === formSelectedTokenAddress) ?? null
        dispatch({ type: 'set-token', payload: token })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formSelectedTokenAddress, tokenBalances])

    return { methods, formSelectedTokenAddress }
}

const getPaymentText = (dpt: DirectPayType) => {
    switch (dpt) {
        case DirectPayType.BillPay:
            return 'Pay to'
        case DirectPayType.OffRamp:
            return 'Transfer to'
        case DirectPayType.SpritzCard:
            return 'Add to'
    }
}

export const CreateDirectPaymentForm = () => {
    const confirmationDialog = useRef<ConfirmationDialogMethods>(null)
    const { fullAccount, closeDialog, type, limits } = useDirectPayContext()
    const { payBill, paying, actionLog, payState } = usePayBill()
    const { connectedNetwork } = useWeb3Connection()

    const { methods, formSelectedTokenAddress } = useSelectToken()

    const amount = methods.watch('amount')
    const rewardsAmount = methods.watch('rewardsAmount')
    const totalAmount = Number(amount ?? 0) + Number(rewardsAmount ?? 0)
    const isDelayed = !!(limits?.delay && totalAmount > limits.delay)

    const handlePayment: SubmitHandler<CreateDirectPayFormData> = async (data) => {
        if (!connectedNetwork) return

        // @ts-ignore
        if (fullAccount?.type && fullAccount.type === BillType.Mortgage) {
            confirmationDialog.current?.confirm(async () => {
                await payBill(data, isDelayed)
            })
        } else {
            await payBill(data, isDelayed)
        }
    }
    const editsDisabled = paying || !formSelectedTokenAddress
    const paymentText = getPaymentText(type)
    const chainInfo = connectedNetwork ? getChainInfo(connectedNetwork) : null

    return (
        <>
            <Form<CreateDirectPayFormData> methods={methods} onSubmit={handlePayment} width="100%">
                <Spacer height={24} />
                <FormItemHeader>
                    <Body.Med secondary size={12}>
                        {paymentText}
                    </Body.Med>
                </FormItemHeader>

                <FullAccountSummary fa={fullAccount} />

                <Spacer height={24} />

                <PaymentAmount disabled={editsDisabled} />

                <DelayIndicator isDelayed={isDelayed} />

                <BalancesIndicator amount={totalAmount} />

                <Spacer height={8} />
                {formSelectedTokenAddress ? (
                    <TokenSelect disabled={editsDisabled} />
                ) : (
                    <TokenSelectEmptyState />
                )}
                <Spacer height={32} />
                <Row align="center">
                    <FormSubmitButton submitting={editsDisabled} />
                </Row>
            </Form>
            <Spacer height={16} />
            <Column>
                <TransactionLogs log={actionLog} />
            </Column>

            {payState?.transaction?.hash && (
                <Row align="center">
                    <Button.Text
                        onClick={() => {
                            if (chainInfo && payState?.transaction?.hash) {
                                window.open(
                                    chainInfo.getExplorerTransactionLink(payState.transaction.hash)
                                )
                            }
                        }}
                    >
                        <Row>
                            View transaction on the blockchain <Spacer width={8} />
                            <Icon icon={OpenNewSvg} />
                        </Row>
                    </Button.Text>
                </Row>
            )}

            <Spacer height={16} />
            <Row align="center">
                <Button.Text onClick={closeDialog} disabled={paying}>
                    Close
                </Button.Text>
            </Row>

            <ConfirmationDialog ref={confirmationDialog}>
                <Body.Bold size={18}>Check your payment amount</Body.Bold>
                <Spacer height={16} />
                <Body.Med size={14} align="center">
                    This provider may only accept payments for the exact amount due. Make sure the
                    amount you entered is correct before sending the transaction to avoid a payment
                    failure.
                </Body.Med>
            </ConfirmationDialog>
        </>
    )
}
